import { DateTime } from "luxon";

import { SearchSettingsDatesOptionsEnum } from "../Models/Settings/PreventiveRecallListSearchSettings";

class PreventiveCareActivitiesHelper {
    private static readonly fiscalYearStartMonth = 4; // April
    private static readonly fiscalYearEndMonth = 3; // March
    private static readonly firstQuarterEnd = 7; // July - 3 months after start

    /**
     * Gets the start date of the current fiscal year.
     * The fiscal year starts in April and ends in March. If the current date is before April,
     * the fiscal year start will be April of the previous year. Otherwise, it will be April of the current year.
     *
     * @returns The start date of the current fiscal year as a `Date` object.
     */
    public static getFiscalYearStart(): Date {
        const now = DateTime.now();
        const year = now.month < this.fiscalYearStartMonth ? now.year - 1 : now.year;
        return DateTime.local(year, this.fiscalYearStartMonth, 1).startOf("day").toJSDate();
    }

    /**
     * Gets the end date of the current fiscal year.
     * The fiscal year ends in March. If the current date is before April,
     * the fiscal year end will be March of the current year. Otherwise, it will be March of the next year.
     *
     * @returns The end date of the current fiscal year as a `Date` object.
     */
    public static getFiscalYearEnd(): Date {
        const now = DateTime.now();
        const year = now.month < this.fiscalYearStartMonth ? now.year : now.year + 1;
        return DateTime.local(year, this.fiscalYearEndMonth, 31).endOf("day").toJSDate();
    }

    /**
     * Gets the start and end date for the report generation, based on the filter type and selected dates.
     * If the filter type is `EndOfFiscalYear` and the current date is within the first quarter of the fiscal year, the previous fiscal year is returned instead.
     *
     * @returns The start and end date to generate the report for.
     */
    public static adjustFiscalYearDatesByFirstQuarterRule(): { dateFrom: Date, dateTo: Date } {
        const { dateFrom: dateFromString, dateTo: dateToString, selectedDateOption } = portfolioStore.preventiveRecallListModel.get("SavedSettingsContainerState");

        if (selectedDateOption !== SearchSettingsDatesOptionsEnum.EndOfFiscalYear) {
            const dateFrom = dateFromString ? new Date(dateFromString) : PreventiveCareActivitiesHelper.getFiscalYearStart();
            const dateTo = dateToString ? new Date(dateToString) : PreventiveCareActivitiesHelper.getFiscalYearEnd();

            return { dateFrom, dateTo };
        }

        const now = DateTime.now();
        const fiscalFrom = PreventiveCareActivitiesHelper.getFiscalYearStart();
        const fiscalTo = PreventiveCareActivitiesHelper.getFiscalYearEnd();

        if (now.month >= this.fiscalYearStartMonth && now.month < this.firstQuarterEnd) {
            fiscalFrom.setFullYear(fiscalFrom.getFullYear() - 1);
            fiscalTo.setFullYear(fiscalTo.getFullYear() - 1);
        }

        return { dateFrom: fiscalFrom, dateTo: fiscalTo };
    }
}

export { PreventiveCareActivitiesHelper };
